import React, { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Plus, Camera, Image } from 'lucide-react';
import documentStorageService from '../../services/documentStorageService';

const MobileUploadModal = ({ 
  isOpen, 
  onClose, 
  onUploadComplete, 
  onError 
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Details, 3: Progress
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file', 'camera'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [documentMetadata, setDocumentMetadata] = useState({
    title: '',
    type: 'general',
    entityType: 'general',
    entityName: '',
    expirationDate: '',
    documentNumber: '',
    issuingAuthority: '',
    notes: ''
  });

  const handleFileSelection = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const isValidType = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ].includes(file.type);
      
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      onError('Some files were skipped due to invalid type or size (max 50MB)');
    }

    setSelectedFiles(validFiles);
    
    if (validFiles.length > 0) {
      // Auto-fill title from first file
      setDocumentMetadata(prev => ({
        ...prev,
        title: prev.title || validFiles[0].name.split('.')[0]
      }));
      setCurrentStep(2);
    }
  };

  const handleFileInput = (e) => {
    handleFileSelection(e.target.files);
  };

  const handleCameraCapture = (e) => {
    handleFileSelection(e.target.files);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    if (selectedFiles.length === 1) {
      setCurrentStep(1);
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentStep(3);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const metadata = {
          ...documentMetadata,
          title: selectedFiles.length > 1 ? `${documentMetadata.title} (${index + 1})` : documentMetadata.title,
          uploadedBy: 'Current User',
          tags: ['mobile-upload'],
          issueDate: new Date().toISOString()
        };

        return await documentStorageService.uploadDocument(
          file,
          metadata,
          {
            enableOCR: documentStorageService.canOCRFile(file),
            onProgress: (progress) => {
              const totalProgress = ((index * 100) + progress) / selectedFiles.length;
              setUploadProgress(Math.round(totalProgress));
            }
          }
        );
      });

      const results = await Promise.all(uploadPromises);
      const successful = results.filter(result => result && result.success !== false);
      
      if (successful.length > 0) {
        onUploadComplete(successful);
        handleClose();
      } else {
        onError('All uploads failed');
        setCurrentStep(2);
      }
    } catch (error) {
      onError('Upload failed: ' + error.message);
      setCurrentStep(2);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedFiles([]);
    setDocumentMetadata({
      title: '',
      type: 'general',
      entityType: 'general',
      entityName: '',
      expirationDate: '',
      documentNumber: '',
      issuingAuthority: '',
      notes: ''
    });
    setUploadProgress(0);
    setIsUploading(false);
    setUploadMethod('file');
    onClose();
  };

  const canProceed = () => {
    return documentMetadata.title && documentMetadata.type;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0">
        <button
          onClick={handleClose}
          disabled={isUploading}
          className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="text-center">
          <h1 className="text-lg font-medium text-gray-900">Upload Document</h1>
          <p className="text-sm text-gray-500">
            Step {currentStep} of 3
          </p>
        </div>
        
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-200 h-1">
        <div 
          className="bg-blue-600 h-1 transition-all duration-300" 
          style={{ width: `${(currentStep / 3) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {currentStep === 1 && (
          <div className="p-4 space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Add Files</h2>
              <p className="text-gray-600">Choose how you'd like to add your document</p>
            </div>

            {/* Upload Methods */}
            <div className="space-y-4">
              {/* File Upload */}
              <button
                onClick={() => {
                  setUploadMethod('file');
                  fileInputRef.current?.click();
                }}
                className="w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors active:bg-gray-50"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileInput}
                  className="hidden"
                />
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Files</h3>
                  <p className="text-sm text-gray-600">
                    PDF, Images, Documents up to 50MB
                  </p>
                </div>
              </button>

              {/* Camera Capture */}
              <button
                onClick={() => {
                  setUploadMethod('camera');
                  cameraInputRef.current?.click();
                }}
                className="w-full p-6 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-400 transition-colors active:bg-blue-50"
              >
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleCameraCapture}
                  className="hidden"
                />
                <div className="text-center">
                  <Camera className="mx-auto h-12 w-12 text-blue-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Take Photo</h3>
                  <p className="text-sm text-gray-600">
                    Use your camera to capture a document
                  </p>
                </div>
              </button>

              {/* Gallery */}
              <button
                onClick={() => {
                  setUploadMethod('gallery');
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.multiple = true;
                  input.onchange = (e) => handleFileSelection(e.target.files);
                  input.click();
                }}
                className="w-full p-6 border-2 border-dashed border-green-300 rounded-lg hover:border-green-400 transition-colors active:bg-green-50"
              >
                <div className="text-center">
                  <Image className="mx-auto h-12 w-12 text-green-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Choose from Gallery</h3>
                  <p className="text-sm text-gray-600">
                    Select photos from your device
                  </p>
                </div>
              </button>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Selected Files ({selectedFiles.length})</h3>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {Math.round(file.size / 1024)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div className="p-4 space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Document Details</h2>
              <p className="text-gray-600">Fill in the information for your document</p>
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={documentMetadata.title}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter document name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={documentMetadata.type}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="license">License</option>
                  <option value="medical">Medical Certificate</option>
                  <option value="insurance">Insurance</option>
                  <option value="registration">Registration</option>
                  <option value="inspection">Inspection</option>
                  <option value="certification">Certification</option>
                  <option value="training">Training Certificate</option>
                  <option value="permit">Permit</option>
                  <option value="maintenance">Maintenance Record</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Entity Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Applies To
                </label>
                <select
                  value={documentMetadata.entityType}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, entityType: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  <option value="company">Company</option>
                  <option value="driver">Driver</option>
                  <option value="vehicle">Vehicle</option>
                </select>
              </div>

              {/* Entity Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity Name
                </label>
                <input
                  type="text"
                  value={documentMetadata.entityName}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, entityName: e.target.value }))}
                  placeholder="e.g., John Smith, Truck TR-001"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={documentMetadata.expirationDate}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, expirationDate: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Document Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Number
                </label>
                <input
                  type="text"
                  value={documentMetadata.documentNumber}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, documentNumber: e.target.value }))}
                  placeholder="e.g., INV-2024-001"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={documentMetadata.notes}
                  onChange={(e) => setDocumentMetadata(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Additional notes or comments..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="p-4 space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Uploading...</h2>
              <p className="text-gray-600">Please wait while we upload your documents</p>
            </div>

            {/* Upload Progress */}
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Upload className="h-6 w-6 text-blue-600 mr-3" />
                  <div>
                    <p className="font-medium text-blue-900">
                      Uploading {selectedFiles.length} document{selectedFiles.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-blue-700">{uploadProgress}% complete</p>
                  </div>
                </div>
                
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>

              {/* File List */}
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{Math.round(file.size / 1024)} KB</p>
                      </div>
                    </div>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {currentStep < 3 && (
        <div className="bg-white border-t border-gray-200 p-4 space-y-3">
          {currentStep === 1 && selectedFiles.length > 0 && (
            <button
              onClick={() => setCurrentStep(2)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Next: Add Details
            </button>
          )}
          
          {currentStep === 2 && (
            <div className="space-y-3">
              <button
                onClick={handleUpload}
                disabled={!canProceed()}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload Document{selectedFiles.length !== 1 ? 's' : ''}
              </button>
              <button
                onClick={() => setCurrentStep(1)}
                className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileUploadModal;